<template>
  <ValidationObserver
    ref="webhookObserver"
    v-slot="{ valid, pristine }" >
    <b-modal
      id="webhook-modal"
      ref="webhookModal"
      modal-class="settings-modal"
      centered
      hide-header-close
      :title="modalTitle"
      size="sm"
      @hidden="resetFields"
      @shown="focus" >
      <div>
        <ValidationProvider
          v-slot="{ errors, dirty }"
          name="URL"
          rules="required|urlPrefixed"
          mode="eager" >
          <b-form-group>
            <label
              for="p-key"
              class="settings__label mt-0" >
              URL
            </label>
            <input
              ref="urlInputRef"
              v-model="url"
              type="text"
              class="w-100 sb2-bordered sb2-hoverable sb2-focusable"
              placeholder="https://" >
            <div
              v-if="errors[0] && dirty"
              class="error flex flex-align-center">
              <SBIcon
                class="mr-2"
                :icon="SBIconList.ERROR"
                :color="SBColors.redPrimary"
                :size="12" />
              <span>{{ errors[0] }}</span>
            </div>
          </b-form-group>
        </ValidationProvider>

        <ValidationProvider
          v-slot="{ errors, dirty }"
          rules="required">
          <b-form-group>
            <label
              for="p-key"
              class="settings__label" >
              Triggers
            </label>
            <div class="mt-3" />
            <b-form-checkbox-group
              v-model="selectedWebhookTypes"
              stacked >

              <div
                v-for="(webhooks, category) in webhookCategories"
                :key="category">
                <div class="mt-3" />
                <label
                  class="settings__label-sub" >
                  {{ category }}
                </label>
                <b-form-checkbox
                  v-for="(webhook, index) in webhooks"
                  :key="index"
                  :value="webhook">{{ webhook }}</b-form-checkbox>
              </div>
            </b-form-checkbox-group>
            <div
              v-if="errors[0] && dirty"
              class="error flex flex-align-center">
              <SBIcon
                class="mr-2"
                :icon="SBIconList.ERROR"
                :color="SBColors.redPrimary"
                :size="12" />
              <span>Event(s) must be selected</span>
            </div>
          </b-form-group>
        </ValidationProvider>

        <div
          v-if="validationErrors"
          class="error-message fs-14 mt-3 flex flex-align-center px-0" >
          <SBIcon
            class="mr-2"
            :icon="SBIconList.ERROR"
            :color="SBColors.redPrimary"
            :size="12" />
          Error saving data. Please try again later.
        </div>
      </div>

      <template slot="modal-footer">
        <b-button
          :disabled="saving"
          @click="hide">
          Cancel
        </b-button>
        <b-button
          variant="primary"
          :disabled="saving || !valid || pristine"
          :class="{loading: saving}"
          class="flex settings__actions--save"
          @click="commitUpdate">
          <span>Save</span>
          <Spinner
            v-if="saving"
            :size="14"
            :thickness="2"
            :color="SBColors.white" />
        </b-button>
      </template>
    </b-modal>
  </ValidationObserver>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import ToasterMixin from '@/mixins/Toaster';
import SBIconList from '@/helpers/lists/sb-icon-list';
import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import Spinner from '@/components/common/Spinner';
import setValidationErrors from '@/helpers/validationErrors';

export default {
  name: 'WebhookModal',
  components: {
    SBIcon,
    Spinner,
  },
  mixins: [
    ToasterMixin,
  ],
  props: {
    businessId: {
      type: String,
      required: true,
      default: '',
    },
  },
  data() {
    return {
      url: '',
      selectedWebhookTypes: [],
      saving: false,
      validationErrors: null,
      update: false,
      activeWebhook: true,
      webhookId: null,
      modalTitle: null,
      SBIconList,
      SBColors,
    };
  },
  computed: {
    ...mapGetters({
      webhookCategories: 'webhook/webhookTypes',
      webhookSaved: 'webhook/webhookSaved',
      apiError: 'webhook/apiError',
    }),
  },
  watch: {
    webhookSaved(saved) {
      this.saving = false;
      if (saved && this.$refs.webhookModal.isVisible) {
        this.$emit('saved', saved);
        this.showToaster(this.update ? 'Webhook has been successfully updated.' : 'Webhook has been successfully created.', null, null, 'OK');
        this.hide();
      }
    },
    apiError(error) {
      this.saving = false;
      if (error.status === 422) {
        this.validationErrors = setValidationErrors(error.data.errors);
      } else {
        this.errorMessage = error.data.message;
      }
    },
  },
  methods: {
    ...mapActions({
      createWebhook: 'webhook/createWebhook',
      updateWebhook: 'webhook/updateWebhook',
    }),
    resetFields() {
      this.update = false;
      this.url = '';
      this.selectedWebhookTypes = [];
      this.activeWebhook = true;
      this.webhookId = null;
      this.saving = false;
      this.validationErrors = null;
    },
    commitUpdate() {
      this.$refs.webhookObserver.validate()
        .then((valid) => {
          if (valid) {
            this.saving = true;
            this.validationErrors = null;
            const data = {
              active: this.activeWebhook,
              url: this.url,
              types: this.selectedWebhookTypes,
            };

            if (this.update) {
              this.updateWebhook({
                merchant_id: this.businessId,
                webhook_id: this.webhookId,
                data,
              });
            } else {
              this.createWebhook({
                merchant_id: this.businessId,
                data,
              });
            }
          }
        });
    },
    show(webHook = null) {
      this.modalTitle = 'New webhook';
      if (webHook) {
        this.modalTitle = 'Edit webhook';
        this.update = true;
        this.url = webHook.url;
        this.selectedWebhookTypes = webHook.selected_webhooks;
        this.activeWebhook = webHook.active;
        this.webhookId = webHook.id;
      }
      this.$refs.webhookModal.show();
    },
    hide() {
      this.saving = false;
      this.$refs.webhookModal.hide();
    },
    focus() {
      if (this.$refs.urlInputRef) {
        this.$refs.urlInputRef.focus();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.custom-checkbox {
  margin-left: 16px;
}
</style>
